// The bet-builder (same-match acca) wiring: how a suggestion that should be placed
// via `/Betting/PlaceBet` is recognised and turned into the legs `placeBetBuilder`
// needs.
//
// A same-match express arrives from the backend as a `parlay` whose legs ALL share
// one `event_id` (ranking.py serialises `tier_lines.assign`'s expresses that way).
// The multi-match acca also arrives as a `parlay`, but its legs span DIFFERENT
// events — so "all legs same event" is the discriminator, not the type.

import type { Suggestion } from "./types";
import { partOf } from "./adapter";
import type { BetBuilderBoard, BetBuilderLeg } from "./place-bet";

/** True when this suggestion is a same-match bet-builder combo (place via
 *  `placeBetBuilder`), false for a single or a multi-match acca (the `toggleStake`
 *  path). */
export function isBetBuilder(s: Suggestion): boolean {
  const legs = s.legs;
  if (s.type !== "parlay" || !legs || legs.length < 2) return false;
  const first = legs[0]?.event_id;
  if (!first) return false;
  return legs.every((l) => l.event_id === first);
}

/** Match a bet-builder suggestion's legs to the raw stakes from
 *  `GetBetBuilderStakesByEvent`, returning the full `BetBuilderLeg[]` (stake code,
 *  type, argument) in the suggestion's leg order.
 *
 *  ⛔ TWO KEYS, in this order, because a stake id is NOT a stable handle on an
 *  outcome. A market taken down and re-opened comes back under a NEW id, and a leg
 *  keyed only by the old one is DROPPED — which is the operator refusing the whole
 *  combo. The outcome's own identity survives the rotation, so a leg whose stake id
 *  is gone from the board is matched by `(stakeTypeId, stakeCode, argument)` — the
 *  same triple the coupon's `CheckedStakesList` names a leg by.
 *
 *  A leg that matches NOTHING is dropped (it cannot be placed, and a zero-code leg
 *  must never leak into the coupon), and so is an AMBIGUOUS one: two board stakes
 *  answering to the same identity is a board we do not understand, and guessing
 *  between them would place a bet the user was never shown. */
export function buildBetBuilderLegs(
  s: Suggestion,
  board: BetBuilderBoard,
): BetBuilderLeg[] {
  const byId = new Map<number, BetBuilderLeg>();
  const byOutcome = new Map<string, BetBuilderLeg | null>();
  for (const st of board.stakes) {
    byId.set(st.stakeId, st);
    const key = outcomeKey(st.stakeTypeId, st.stakeCode, st.argument, partOf(st));
    // `null` marks a key two different stakes answer to — see the ambiguity note.
    const seen = byOutcome.get(key);
    byOutcome.set(key, seen === undefined ? st : (seen?.stakeId === st.stakeId ? seen : null));
  }
  const out: BetBuilderLeg[] = [];
  for (const leg of s.legs ?? []) {
    const stakeId = Number(leg.stake_id);
    const direct = Number.isFinite(stakeId) && stakeId > 0 ? byId.get(stakeId) : undefined;
    if (direct) {
      out.push(direct);
      continue;
    }
    const code = leg.code;
    const marketId = leg.market_id;
    if (code == null || marketId == null) continue;
    const meta = byOutcome.get(outcomeKey(
      Number(marketId), Number(code), leg.line == null ? null : Number(leg.line),
      // ⛔⛔ THE SUB-EVENT IS PART OF THE OUTCOME'S IDENTITY. Since the board
      // pools every sub-event, `(3, 1, 9.5)` matches BOTH a goals Over 9.5 and
      // a corners Over 9.5 — measured, they are byte-identical triples. Without
      // the part this fallback would place whichever one happened to be indexed
      // last, silently, on a rotated stake id.
      normPart(leg.part),
    ));
    if (meta) out.push(meta);
  }
  return out;
}

/** `(market, code, line, part)` as one comparable key. `null` argument and 0 are
 *  NOT the same thing — a moneyline HAS no line, and folding the two together
 *  matches a market we cannot name. `part` is the sub-event, `""` on the main
 *  book — see `buildBetBuilderLegs`. */
function outcomeKey(
  marketId: number, code: number, argument: number | null | undefined, part: string,
): string {
  return `${marketId}:${code}:${argument == null ? "n" : Number(argument)}|${part}`;
}

/** A leg's `part` in the board's spelling: `""` for the main book, so a leg the
 *  backend sent without one (or with an explicit "0:0") matches a main-book
 *  stake, which carries no `subEvent`. */
function normPart(part: string | null | undefined): string {
  const p = String(part ?? "").trim();
  return !p || p === "0:0" ? "" : p;
}
